import React, { FC, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { FormattedMessage, useIntl } from 'react-intl';
import cn from 'classnames';
import useModalStore from 'src/hooks/useModalStore';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import { ModalPosition, ModalRounding } from '../Modal/Modal';
import trns from './trns';
import ButtonBase, { ButtonColor, ButtonSize } from '../Button';
import usePostSubscription from '../../api/hooks/usePostSubscription';
import useNotification, { NotificationType } from '../../hooks/useNotification';

const Modal = dynamic(() => import('../Modal/Modal'));
interface Props {
  gtagPrefix?: GTAGPrefix;
}

const Subscription: FC<Props> = ({ gtagPrefix }) => {
  const intl = useIntl();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { saveEmailSubscription, isLoading } = usePostSubscription();
  const { updateModalsConfig } = useModalStore();
  const { showNotification } = useNotification();
  const { trackGtagEvent } = useGTAG();

  const [emailInput, setEmail] = useState<string>('');
  const [formErrors, setFormErrors] = useState({ email: '' });

  const closeModal = () => {
    setEmail('');
    updateModalsConfig('subscription-modal', false);
    setFormErrors({ email: '' });
  };
  const handleInputChange = (event) => {
    setEmail(event.target.value);
  };

  useEffect(() => {
    if (isLoading) {
      updateModalsConfig('subscription-modal', true);
    }
  }, [isLoading]);

  const openSubscriptionModal = async () => {
    if (emailInput) {
      await saveEmailSubscription({ email: emailInput });
      trackGtagEvent(`${gtagPrefix}_subscribe_success`);
    }
  };

  useEffect(() => {
    if (formErrors?.email) {
      showNotification({
        message: formErrors.email || '',
        type: NotificationType.Error
      });
    } else {
      openSubscriptionModal();
    }
  }, [formErrors?.email]);

  const isEmailValid = (email: string) => {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      email
    );
  };

  const validateFormik = (value) => {
    const errors = {
      email: null
    };

    // email validation
    if (!value) {
      errors.email = intl.formatMessage(trns.requiredFieldLabel);
    } else if (!isEmailValid(value)) {
      errors.email = intl.formatMessage(trns.incorrectEmail);
    }

    if (!errors.email) {
      return null;
    }
    return errors;
  };

  const handleSubmit = async () => {
    setFormErrors(validateFormik(emailInput));
    trackGtagEvent(`${gtagPrefix}_subscribe`);
  };

  return (
    <div>
      <div className="relative w-full h-full flex">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-row w-full bg-white border border-astronautBlue rounded-xl">
          <div className={cn('w-9/12')}>
            <input
              placeholder={intl.formatMessage(trns.placeholder)}
              value={emailInput}
              name="email"
              className={cn(
                'rounded-xl px-8',
                'outline-none w-full pt-2 pb-2 h-10 text-gray900 placeholder-silver text-ellipsis compendium-placeholder-italic placeholder:fs-caption-reg fs-caption-reg'
              )}
              onChange={handleInputChange}
            />
          </div>
          <ButtonBase
            color={ButtonColor.blue}
            size={ButtonSize.lg}
            onClick={handleSubmit}
            className="flex h-[40] items-center justify-center rounded-r-xl fs-body-reg"
            innerClassName="rounded-l-none rounded-r-xl h-10"
            label={
              <p className="uppercase fs-caption-reg">
                <FormattedMessage {...trns.inputButtonTitle} />
              </p>
            }
          />
        </div>
      </div>
      <Modal
        id="subscription-modal"
        position={ModalPosition.AbsoluteCenter}
        roundingMode={ModalRounding.All}
        triggerElementRef={buttonRef}
        onClose={closeModal}
      >
        <div className="flex flex-col h-full w-[329px]">
          <p className="text-astronautBlue text-center fs-header6-bold pb-8">
            <FormattedMessage {...trns.titleModal} />
          </p>
          <div className="text-mako fs-body-reg text-center pb-9">
            <FormattedMessage {...trns.infoModal} />
          </div>
          <ButtonBase
            onClick={closeModal}
            color={ButtonColor.blue}
            size={ButtonSize.md}
            className="flex w-full items-center justify-center pb-[37px]"
            innerClassName="w-1/2"
            label={
              <p className="uppercase fs-caption-reg">
                <FormattedMessage {...trns.titleButton} />
              </p>
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default Subscription;
